<script setup>
import { computed, reactive, ref, watch, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useWindowSize } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import Draggable from 'vuedraggable';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';
import wootConstants from 'dashboard/constants/globals';
import { filterItemsByPermission, getUserPermissions } from 'dashboard/helper/permissionsHelper.js';
import { ASSIGNEE_TYPE_TAB_PERMISSIONS } from 'dashboard/constants/permissions.js';
import filterQueryGenerator from 'dashboard/helper/filterQueryGenerator.js';
import ConversationCard from 'dashboard/components/widgets/conversation/ConversationCard.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import ConversationBasicFilter from 'dashboard/components/widgets/conversation/ConversationBasicFilter.vue';
import ChatTypeTabs from 'dashboard/components/widgets/ChatTypeTabs.vue';
import TeleportWithDirection from 'dashboard/components-next/TeleportWithDirection.vue';
import ConversationFilter from 'next/filter/ConversationFilter.vue';
import types from 'dashboard/store/mutation-types';

const store = useStore();
const { width } = useWindowSize();
const { t } = useI18n({
  useScope: 'global',
});
const alert = useAlert;

const chatLists = useMapGetter('getFilteredConversations');
const chatListLoading = useMapGetter('getChatListLoadingStatus');
const appliedFilters = useMapGetter('getAppliedConversationFiltersV2');
const conversationStats = useMapGetter('conversationStats/getStats');
const currentUser = useMapGetter('getCurrentUser');
const currentAccountId = useMapGetter('getCurrentAccountId');

const STATUS_ORDER = [
  {
    key: wootConstants.STATUS_TYPE.PENDING,
    labelKey: 'CONVERSATION.PIPELINE.PENDING',
    badge: 'bg-n-amber-3 text-n-amber-11',
  },
  {
    key: wootConstants.STATUS_TYPE.OPEN,
    labelKey: 'CONVERSATION.PIPELINE.OPEN',
    badge: 'bg-n-blue-3 text-n-blue-11',
  },
  {
    key: wootConstants.STATUS_TYPE.SNOOZED,
    labelKey: 'CONVERSATION.PIPELINE.SNOOZED',
    badge: 'bg-n-slate-3 text-n-slate-11',
  },
  {
    key: wootConstants.STATUS_TYPE.RESOLVED,
    labelKey: 'CONVERSATION.PIPELINE.RESOLVED',
    badge: 'bg-n-green-3 text-n-green-11',
  },
];

const columnsState = reactive({
  [wootConstants.STATUS_TYPE.OPEN]: [],
  [wootConstants.STATUS_TYPE.PENDING]: [],
  [wootConstants.STATUS_TYPE.SNOOZED]: [],
  [wootConstants.STATUS_TYPE.RESOLVED]: [],
});

const showAdvancedFilters = ref(false);
const filterDraft = ref([]);
const activeAssigneeTab = ref(wootConstants.ASSIGNEE_TYPE.ALL);
const activeStatus = ref(wootConstants.STATUS_TYPE.ALL);
const activeSortBy = ref(wootConstants.SORT_BY_TYPE.LAST_ACTIVITY_AT_DESC);

const isSmallScreen = computed(
  () => width.value <= wootConstants.SMALL_SCREEN_BREAKPOINT
);
const buttonSize = computed(() => (isSmallScreen.value ? 'md' : 'xs'));
const filterTeleportClass = computed(() =>
  isSmallScreen.value
    ? 'fixed inset-x-4 top-20 z-50'
    : 'absolute z-50 mt-2 ltr:right-0 rtl:left-0'
);
const filterTeleportTarget = computed(() =>
  isSmallScreen.value ? 'body' : '#conversationFilterTeleportTarget'
);

const hasAppliedFilters = computed(
  () => appliedFilters.value.length !== 0
);

const conversationFilters = computed(() => ({
  assigneeType: activeAssigneeTab.value,
  status: activeStatus.value,
  sortBy: activeSortBy.value,
  page: 1,
}));

const userPermissions = computed(() =>
  getUserPermissions(currentUser.value, currentAccountId.value)
);


const assigneeTabItems = computed(() => {
  const allowedItems = filterItemsByPermission(
    ASSIGNEE_TYPE_TAB_PERMISSIONS,
    userPermissions.value,
    item => item.permissions
  );

  return allowedItems.map(({ key, count: countKey }) => ({
    key,
    name: t(`CHAT_LIST.ASSIGNEE_TYPE_TABS.${key}`),
    count: conversationStats.value[countKey] || 0,
  }));
});

watch(
  assigneeTabItems,
  newItems => {
    if (!newItems.length) {
      return;
    }
    const stillValid = newItems.some(
      item => item.key === activeAssigneeTab.value
    );
    if (!stillValid) {
      activeAssigneeTab.value = newItems[0].key;
    }
  },
  { immediate: true }
);

const conversationSource = computed(() => {
  if (hasAppliedFilters.value) {
    return chatLists.value || [];
  }

  const filters = conversationFilters.value;

  if (activeAssigneeTab.value === wootConstants.ASSIGNEE_TYPE.ME) {
    return store.getters.getMineChats(filters);
  }
  if (activeAssigneeTab.value === wootConstants.ASSIGNEE_TYPE.UNASSIGNED) {
    return store.getters.getUnAssignedChats(filters);
  }
  return store.getters.getAllStatusChats(filters);
});

watch(
  conversationSource,
  conversations => {
    STATUS_ORDER.forEach(column => {
      columnsState[column.key] = conversations.filter(
        conversation => conversation.status === column.key
      );
    });
  },
  { immediate: true }
);

const isLoading = computed(() => chatListLoading.value);

const shouldShowTabs = computed(() => !hasAppliedFilters.value);
const shouldShowBasicFilters = computed(() => !hasAppliedFilters.value);
const statusChipLabel = computed(() =>
  t(`CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.${activeStatus.value}.TEXT`)
);

const fetchConversationStats = () => {
  if (hasAppliedFilters.value) {
    return;
  }
  store.dispatch('conversationStats/get', conversationFilters.value);
};

const fetchConversations = () => {
  if (hasAppliedFilters.value) {
    return;
  }
  store.dispatch('updateChatListFilters', conversationFilters.value);
  store.dispatch('fetchAllConversations');
  fetchConversationStats();
};

const resetAndFetchData = () => {
  if (hasAppliedFilters.value) {
    return;
  }
  store.dispatch('conversationPage/reset');
  store.dispatch('emptyAllConversations');
  fetchConversations();
};

const rebuildFilteredConversations = () => {
  const filters = appliedFilters.value;
  if (!filters.length) {
    resetAndFetchData();
    return;
  }
  const transformed = useSnakeCase(filters);
  store.dispatch('conversationPage/reset');
  store.dispatch('emptyAllConversations');
  store.dispatch('fetchFilteredConversations', {
    queryData: filterQueryGenerator(transformed),
    page: 1,
  });
};

const handleStatusChange = async (conversation, targetStatus) => {
  if (conversation.status === targetStatus) {
    return;
  }
  const previousStatus = conversation.status;
  const previousSnoozedUntil = conversation.snoozed_until;
  store.commit(types.CHANGE_CONVERSATION_STATUS, {
    conversationId: conversation.id,
    status: targetStatus,
    snoozedUntil: previousSnoozedUntil,
  });
  try {
    await store.dispatch('toggleStatus', {
      conversationId: conversation.id,
      status: targetStatus,
    });
    if (hasAppliedFilters.value) {
      rebuildFilteredConversations();
    } else {
      fetchConversationStats();
    }
  } catch (error) {
    store.commit(types.CHANGE_CONVERSATION_STATUS, {
      conversationId: conversation.id,
      status: previousStatus,
      snoozedUntil: previousSnoozedUntil,
    });
    alert(t('CONVERSATION.PIPELINE.ERROR_UPDATING'));
    if (hasAppliedFilters.value) {
      rebuildFilteredConversations();
    } else {
      fetchConversations();
    }
  }
};

const onColumnChange = (event, columnStatus) => {
  if (event?.added?.element) {
    handleStatusChange(event.added.element, columnStatus);
  }
};

const handleTabChange = tabKey => {
  if (activeAssigneeTab.value === tabKey) {
    return;
  }
  activeAssigneeTab.value = tabKey;
};

const onBasicFilterChange = (value, type) => {
  if (type === 'status') {
    activeStatus.value = value;
  } else {
    activeSortBy.value = value;
  }
};

const getDefaultFilterDraft = () => {
  const status = activeStatus.value;
  const values =
    status && status !== wootConstants.STATUS_TYPE.ALL ? [status] : [];
  return [
    {
      attributeKey: 'status',
      filterOperator: 'equal_to',
      values,
      queryOperator: 'and',
    },
  ];
};
filterDraft.value = getDefaultFilterDraft();

const toggleFiltersModal = () => {
  if (showAdvancedFilters.value) {
    showAdvancedFilters.value = false;
    return;
  }
  filterDraft.value = appliedFilters.value.length
    ? JSON.parse(JSON.stringify(appliedFilters.value))
    : getDefaultFilterDraft();
  showAdvancedFilters.value = true;
};

const closeFiltersModal = () => {
  showAdvancedFilters.value = false;
};

const handleApplyFilter = payload => {
  const transformed = useSnakeCase(payload);
  store.dispatch('conversationPage/reset');
  store.dispatch('emptyAllConversations');
  store
    .dispatch('fetchFilteredConversations', {
      queryData: filterQueryGenerator(transformed),
      page: 1,
    })
    .then(() => {
      showAdvancedFilters.value = false;
    });
};

const clearFilters = () => {
  store.dispatch('conversationPage/reset');
  store.dispatch('emptyAllConversations');
  store.dispatch('clearConversationFilters');
  filterDraft.value = getDefaultFilterDraft();
  showAdvancedFilters.value = false;
  resetAndFetchData();
};

watch(
  [activeAssigneeTab, activeStatus, activeSortBy],
  () => {
    if (hasAppliedFilters.value) {
      return;
    }
    resetAndFetchData();
  },
  { flush: 'post' }
);

watch(hasAppliedFilters, newValue => {
  if (!newValue) {
    filterDraft.value = getDefaultFilterDraft();
    showAdvancedFilters.value = false;
    resetAndFetchData();
  }
});

onMounted(() => {
  store.dispatch('setChatListFilters', conversationFilters.value);
  store.dispatch('setChatStatusFilter', activeStatus.value);
  store.dispatch('setChatSortFilter', activeSortBy.value);
  resetAndFetchData();
});

</script>

<template>
  <div class="flex flex-col gap-4 w-full h-full overflow-x-hidden px-4 py-4">
    <header class="flex flex-col gap-4 border-b border-n-weak/60 pb-4">
      <div
        class="flex flex-wrap items-center gap-2 md:flex-nowrap md:gap-4"
      >
        <div class="flex items-center gap-2 min-w-0 flex-1">
          <h1 class="text-xl font-semibold text-n-slate-12 truncate">
            {{ t('CONVERSATION.PIPELINE.TITLE') }}
          </h1>
          <span
            v-if="!hasAppliedFilters"
            class="px-2 py-1 my-0.5 rounded-md capitalize bg-n-slate-3 text-xxs text-n-slate-12 shrink-0"
          >
            {{ statusChipLabel }}
          </span>
        </div>
        <div class="flex items-center gap-2 flex-shrink-0">
          <div class="relative">
            <NextButton
              id="toggleConversationFilterButton"
              v-tooltip.right="$t('FILTER.TOOLTIP_LABEL')"
              icon="i-lucide-list-filter"
              slate
              faded
              :size="buttonSize"
              @click="toggleFiltersModal"
            />
            <div
              id="conversationFilterTeleportTarget"
              :class="filterTeleportClass"
            />
          </div>
          <NextButton
            v-if="hasAppliedFilters"
            v-tooltip.right="$t('FILTER.CLEAR_BUTTON_LABEL')"
            icon="i-lucide-circle-x"
            ruby
            faded
            :size="buttonSize"
            @click="clearFilters"
          />
          <ConversationBasicFilter
            v-if="shouldShowBasicFilters"
            :is-on-expanded-layout="false"
            :button-size="buttonSize"
            @change-filter="onBasicFilterChange"
          />
        </div>
      </div>
      <p class="text-sm text-n-slate-11 hidden md:block">
        {{ t('CONVERSATION.PIPELINE.DESCRIPTION') }}
      </p>
      <ChatTypeTabs
        v-if="shouldShowTabs"
        :items="assigneeTabItems"
        :active-tab="activeAssigneeTab"
        @chat-tab-change="handleTabChange"
      />
    </header>

    <div class="flex-1 overflow-auto">
      <div
        class="grid gap-4 auto-rows-fr"
        style="grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));"
      >
        <section
          v-for="column in STATUS_ORDER"
          :key="column.key"
          class="flex flex-col bg-n-solid-1 border border-n-weak rounded-2xl min-h-[320px]"
        >
          <header class="flex items-center justify-between px-4 py-3 border-b border-n-weak/60">
            <div class="flex flex-col">
              <span class="text-sm font-semibold text-n-slate-12">
                {{ t(column.labelKey) }}
              </span>
              <span class="text-xs text-n-slate-11">
                {{
                  t('CONVERSATION.PIPELINE.COUNT_LABEL', {
                    count: columnsState[column.key]?.length || 0,
                  })
                }}
              </span>
            </div>
            <span
              class="px-2 py-0.5 rounded-full text-xxs font-semibold"
              :class="column.badge"
            >
              {{ columnsState[column.key]?.length || 0 }}
            </span>
          </header>

          <div class="flex-1 overflow-y-auto px-3 py-3">
            <div v-if="isLoading" class="flex justify-center py-6">
              <Spinner class="text-n-brand" />
            </div>
            <template v-else>
              <Draggable
                v-model="columnsState[column.key]"
                item-key="id"
                :group="{ name: 'pipeline-conversations', pull: true, put: true }"
                class="flex flex-col gap-3 pb-2"
                ghost-class="opacity-40"
                @change="event => onColumnChange(event, column.key)"
              >
                <template #item="{ element }">
                  <ConversationCard
                    :chat="element"
                    :enable-context-menu="false"
                    compact
                  />
                </template>
              </Draggable>
              <div
                v-if="!columnsState[column.key]?.length"
                class="flex flex-col items-center justify-center text-center text-xs text-n-slate-11 py-10 px-2"
              >
                {{ t('CONVERSATION.PIPELINE.EMPTY_COLUMN') }}
              </div>
            </template>
          </div>
        </section>
      </div>
    </div>

    <TeleportWithDirection
      v-if="showAdvancedFilters"
      :to="filterTeleportTarget"
    >
      <template v-if="isSmallScreen">
        <div class="fixed inset-0 z-[1000] bg-n-alpha-black1/60 backdrop-blur-[2px]">
          <div class="flex flex-col h-full bg-n-solid-1">
            <header class="flex items-center justify-between px-4 py-3 border-b border-n-weak">
              <h3 class="text-base font-medium text-n-slate-12">
                {{ t('FILTER.TITLE') }}
              </h3>
              <NextButton
                icon="i-lucide-x"
                slate
                ghost
                size="md"
                @click="closeFiltersModal"
              />
            </header>
            <div class="flex-1 overflow-y-auto p-4">
              <ConversationFilter
                v-model="filterDraft"
                @apply-filter="handleApplyFilter"
                @close="closeFiltersModal"
              />
            </div>
          </div>
        </div>
      </template>
      <template v-else>
        <ConversationFilter
          v-model="filterDraft"
          @apply-filter="handleApplyFilter"
          @close="closeFiltersModal"
        />
      </template>
    </TeleportWithDirection>
  </div>
</template>
